/**
 * @file useCountersDraft.ts
 * @description Часть COUNTERS черновика вида сайта с автосохранением
 * (phase19.16, партия Р3г, v0.19.62; db-design-r3g §4; SB133;
 * docs/api/site-counters.md).
 *
 * Как у прочих частей (`useAutosavePart`): правка сразу в окне, запись —
 * через паузу после последней правки, `expectedVersion`, 409 — конфликт.
 * Отличия:
 *  - галочка окна о риске: `update({ mode: "notice" }, { noticeAck: true })`
 *    запоминает номер ТОЙ редакции текста окна, которую сотрудник видел
 *    (`consentPlatform.noticeRisk.currentVersion` на момент галочки), и
 *    ближайшее сохранение несёт `noticeAck { version }`. Снимается после
 *    успешной записи (сетевая ошибка и «Повторить» её сохраняют) и при уходе
 *    из «Уведомления»;
 *  - 422 `site_counters_notice_ack_required` (текст окна обновился),
 *    `site_counters_notice_forbidden` (платформа запретила режим) и
 *    `site_counters_text_version_unknown` (нет такой редакции текстов) —
 *    часть перечитывается (`reload`), причина остаётся строкой `notice`
 *    до следующей правки.
 * Номера и вступление сюда попадают уже проверенными полями панели.
 */

import { readCounters, type SiteCounters } from "@chaiberi/shared";
import { useCallback, useMemo, useRef, useState } from "react";

import { useAutosavePart, type AutosaveApi } from "./useAutosavePart";
import type { ApplyPart } from "./useThemeDraft";
import { siteView, type CountersPartResponse } from "../../lib/api-site-view";
import { noticeAllowedOf, noticeRiskOf } from "../../lib/site-view/consent-platform";
import { apiErrorCode } from "../../lib/site-view/errors";
import type { SiteConsentPlatform, SiteViewDraft } from "../../lib/site-view/types";

export interface CountersUpdateOptions {
  /** Поставлена галочка «отвечаю сам» окна о риске (переход в «Уведомление»). */
  noticeAck?: boolean;
}

export interface CountersDraftApi extends AutosaveApi<SiteCounters> {
  counters: SiteCounters;
  /** Политика платформы из черновика; нет — сервер до волны редакций (встроенные тексты). */
  platform: SiteConsentPlatform | undefined;
  /** Разрешён ли платформой режим «Уведомление» (SB133). */
  noticeAllowed: boolean;
  /** Почему часть перечитана с сервера (422 по режиму); null — нечего сказать. */
  notice: string | null;
  update: (patch: Partial<SiteCounters>, options?: CountersUpdateOptions) => void;
}

/** 422, после которых часть перечитывается, и что сказать сотруднику. */
export const COUNTERS_RELOAD_TEXT: Readonly<Record<string, string>> = {
  site_counters_notice_ack_required: "Текст окна о риске обновился — откройте его снова: выберите «Уведомление» ещё раз.",
  site_counters_notice_forbidden: "Платформа запретила режим «Уведомление» — правки не сохранились, черновик перечитан. Выберите «Только после согласия».",
  site_counters_text_version_unknown: "Такой редакции текстов баннера на платформе нет — черновик перечитан. Обновите тексты до текущей редакции.",
};

/** Ответ сервера → значение части и политика платформы для каркаса. */
function fromResponse(res: CountersPartResponse) {
  return {
    part: res.part,
    value: readCounters(res.counters).counters,
    patch: res.consentPlatform === undefined ? {} : { consentPlatform: res.consentPlatform },
  };
}

/** Состояние части; `null` — черновик ещё не загружен. */
export function useCountersDraftState(draft: SiteViewDraft | null, applyPart: ApplyPart): CountersDraftApi | null {
  const ack = useRef<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Политика платформы из последнего ответа — каркасу вместе с частью.
  const platform = useRef<Pick<SiteViewDraft, "consentPlatform">>({});
  const reloadRef = useRef<() => Promise<void>>(async () => undefined);
  const loaded = draft !== null;
  const raw = draft?.counters;
  const serverValue = useMemo(() => (loaded ? readCounters(raw).counters : null), [loaded, raw]);

  const api = useAutosavePart<SiteCounters>({
    serverValue,
    // Части COUNTERS у сервера до Р3г нет — версия 0, правка покажет ошибку сохранения.
    serverVersion: draft?.parts.COUNTERS?.version ?? 0,
    save: async (value, expectedVersion) => {
      const noticeAck = value.mode === "notice" && ack.current !== null ? { version: ack.current } : null;
      try {
        const res = await siteView.saveCounters({ expectedVersion, counters: value, ...(noticeAck ? { noticeAck } : {}) });
        if (noticeAck) ack.current = null;
        const out = fromResponse(res);
        platform.current = out.patch;
        return { part: out.part, value: out.value };
      } catch (err) {
        const text = COUNTERS_RELOAD_TEXT[apiErrorCode(err) ?? ""];
        if (text) {
          ack.current = null;
          setNotice(text);
          // После того как автосохранение разберёт ошибку — перечитать часть и политику платформы.
          setTimeout(() => void reloadRef.current().catch(() => undefined), 0);
        }
        throw err;
      }
    },
    load: async () => {
      const out = fromResponse(await siteView.counters());
      platform.current = out.patch;
      return { part: out.part, value: out.value };
    },
    onSaved: (result) => applyPart(result.part, { counters: result.value, ...platform.current }),
  });
  reloadRef.current = api.reload;

  const counters = api.value ?? serverValue;
  const { change } = api;
  const riskVersion = noticeRiskOf(draft?.consentPlatform).version;
  const update = useCallback(
    (patch: Partial<SiteCounters>, options: CountersUpdateOptions = {}) => {
      if (!counters) return;
      setNotice(null);
      const next: SiteCounters = { ...counters, ...patch };
      if (next.mode !== "notice") ack.current = null;
      else if (options.noticeAck) ack.current = riskVersion;
      change(next, Object.keys(patch));
    },
    [change, counters, riskVersion],
  );

  if (!draft || !counters) return null;
  const consentPlatform = draft.consentPlatform;
  return { ...api, counters, platform: consentPlatform, noticeAllowed: noticeAllowedOf(consentPlatform), notice, update };
}
